/**
 * Generic sessionStorage utility for Two-Factor Authentication state management
 * Supports multiple namespaces (login, settings, switch, disable) to avoid key conflicts
 * Used for persisting state across navigation
 * This is a generic solution that can be used for any two-factor authentication flow
 */

/**
 * Centralized rate limiting constants for 2FA
 * These values should match the backend (Authentication.php):
 * - MAX_VERIFY_ATTEMPTS = 4, VERIFY_LOCKOUT_MINUTES = 15
 * - MAX_EMAIL_SEND_ATTEMPTS = 3, EMAIL_SEND_LOCKOUT_MINUTES = 10
 *
 * Timer durations can be overridden via .env.local for development.
 * See env.ts for available VUE_APP_TWOFACTOR_* variables.
 */
import Env from '@/env'

export const TWOFACTOR_RATE_LIMITS = {
    // OTP code verification: 4 wrong attempts → 15-minute lockout
    VERIFY_MAX_ATTEMPTS: 4,
    VERIFY_LOCKOUT_SECONDS: Env.twoFactorVerifyLockoutSeconds,

    // Email send button: 3 attempts → 10-minute lockout (handled by backend)
    EMAIL_SEND_MAX_ATTEMPTS: 3,
    EMAIL_SEND_LOCKOUT_SECONDS: Env.twoFactorEmailLockoutSeconds,

    // Resend link cooldown: 2 minutes between resends
    RESEND_COOLDOWN_SECONDS: Env.twoFactorResendCooldownSeconds,

    // OTP digit count - used for input fields and validation
    OTP_DIGIT_COUNT: 6
}

/**
 * Creates an empty OTP digits array with the correct length
 */
export function createEmptyOtpDigits(): string[] {
    return Array(TWOFACTOR_RATE_LIMITS.OTP_DIGIT_COUNT).fill('')
}

export type TwoFactorMethod = 'totp' | 'email'

export interface TwoFactorLoginState {
    consumerId: string
    method: TwoFactorMethod
    maskedEmail?: string
    emailSent?: boolean
    // Lockout seconds directly from the database (no client-side end time calculation)
    verifyLockoutSeconds?: number
    emailLockoutSeconds?: number
}

export interface LockoutState {
    isActive: boolean
    remainingSeconds: number
    duration: number
}

export interface RateLimitState {
    isActive: boolean
    remainingSeconds: number
    duration: number
    message: string
}

export interface ResendCooldownState {
    isActive: boolean
    remainingSeconds: number
    duration: number
}

export class TwoFactorStorage {
    private readonly namespace: string

    constructor(namespace: string = '') {
        this.namespace = namespace ? `${namespace}_` : ''
    }

    /**
     * Save login state object
     */
    saveState(state: TwoFactorLoginState): void {
        const key = this.getKey('state')
        sessionStorage.setItem(key, JSON.stringify(state))
    }

    /**
     * Get login state object
     */
    getState(): TwoFactorLoginState | null {
        const key = this.getKey('state')
        const item = sessionStorage.getItem(key)
        if (!item) return null

        try {
            return JSON.parse(item) as TwoFactorLoginState
        } catch {
            return null
        }
    }

    /**
     * Remove login state
     */
    removeState(): void {
        const key = this.getKey('state')
        sessionStorage.removeItem(key)
    }

    /**
     * Save lockout with end time and duration
     */
    saveLockout(durationSeconds: number): void {
        const endTime = Date.now() + durationSeconds * 1000
        const endTimeKey = this.getKey('lockout_end_time')
        const durationKey = this.getKey('lockout_duration')

        sessionStorage.setItem(endTimeKey, endTime.toString())
        sessionStorage.setItem(durationKey, durationSeconds.toString())
    }

    /**
     * Get lockout state with calculated remaining time
     */
    getLockout(): LockoutState | null {
        const endTimeKey = this.getKey('lockout_end_time')
        const durationKey = this.getKey('lockout_duration')

        const endTimeStr = sessionStorage.getItem(endTimeKey)
        const durationStr = sessionStorage.getItem(durationKey)

        if (!endTimeStr) return null

        const endTime = parseInt(endTimeStr, 10)
        const now = Date.now()

        if (endTime <= now) {
            // Lockout expired - clean up
            this.removeLockout()
            return null
        }

        const remainingSeconds = Math.ceil((endTime - now) / 1000)
        const duration = durationStr ? parseInt(durationStr, 10) : remainingSeconds

        return {
            isActive: true,
            remainingSeconds,
            duration
        }
    }

    removeLockout(): void {
        const endTimeKey = this.getKey('lockout_end_time')
        const durationKey = this.getKey('lockout_duration')
        sessionStorage.removeItem(endTimeKey)
        sessionStorage.removeItem(durationKey)
    }

    /**
     * Save rate limit with end time and message (duration in minutes)
     */
    saveRateLimit(durationMinutes: number, message: string): void {
        const totalSeconds = durationMinutes * 60
        this.saveRateLimitSeconds(totalSeconds, message)
    }

    /**
     * Save rate limit with end time and message (duration in seconds)
     * Use this when you have exact seconds from the backend to avoid rounding
     */
    saveRateLimitSeconds(durationSeconds: number, message: string): void {
        const endTime = Date.now() + durationSeconds * 1000
        const endTimeKey = this.getKey('rate_limit_end_time')
        const messageKey = this.getKey('rate_limit_message')

        sessionStorage.setItem(endTimeKey, endTime.toString())
        sessionStorage.setItem(messageKey, message)
    }

    /**
     * Get rate limit state with calculated remaining time
     */
    getRateLimit(): RateLimitState | null {
        const endTimeKey = this.getKey('rate_limit_end_time')
        const messageKey = this.getKey('rate_limit_message')

        const endTimeStr = sessionStorage.getItem(endTimeKey)
        const message = sessionStorage.getItem(messageKey) || ''

        if (!endTimeStr) return null

        const endTime = parseInt(endTimeStr, 10)
        const now = Date.now()

        if (endTime <= now) {
            // Rate limit expired - clean up
            this.removeRateLimit()
            return null
        }

        const remainingSeconds = Math.ceil((endTime - now) / 1000)

        return {
            isActive: true,
            remainingSeconds,
            duration: remainingSeconds, // Use remaining as duration for progress calculation
            message
        }
    }

    removeRateLimit(): void {
        const endTimeKey = this.getKey('rate_limit_end_time')
        const messageKey = this.getKey('rate_limit_message')
        sessionStorage.removeItem(endTimeKey)
        sessionStorage.removeItem(messageKey)
    }

    /**
     * Save resend cooldown with end time and duration
     */
    saveResendCooldown(durationSeconds: number): void {
        const endTime = Date.now() + durationSeconds * 1000
        const endTimeKey = this.getKey('resend_cooldown_end_time')
        const durationKey = this.getKey('resend_cooldown_duration')

        sessionStorage.setItem(endTimeKey, endTime.toString())
        sessionStorage.setItem(durationKey, durationSeconds.toString())
    }

    /**
     * Get resend cooldown state with calculated remaining time
     */
    getResendCooldown(): ResendCooldownState | null {
        const endTimeKey = this.getKey('resend_cooldown_end_time')
        const durationKey = this.getKey('resend_cooldown_duration')

        const endTimeStr = sessionStorage.getItem(endTimeKey)
        const durationStr = sessionStorage.getItem(durationKey)

        if (!endTimeStr) return null

        const endTime = parseInt(endTimeStr, 10)
        const now = Date.now()

        if (endTime <= now) {
            // Cooldown expired - clean up
            this.removeResendCooldown()
            return null
        }

        const remainingSeconds = Math.ceil((endTime - now) / 1000)
        const duration = durationStr ? parseInt(durationStr, 10) : remainingSeconds

        return {
            isActive: true,
            remainingSeconds,
            duration
        }
    }

    removeResendCooldown(): void {
        const endTimeKey = this.getKey('resend_cooldown_end_time')
        const durationKey = this.getKey('resend_cooldown_duration')
        sessionStorage.removeItem(endTimeKey)
        sessionStorage.removeItem(durationKey)
    }

    clearAll(): void {
        this.removeState()
        this.removeLockout()
        this.removeRateLimit()
        this.removeResendCooldown()
    }

    /**
     * Clear only the main state, preserving lockout/rate limit
     */
    clearStateOnly(): void {
        this.removeState()
    }

    /**
     * Get namespaced key
     */
    private getKey(suffix: string): string {
        return `twofactor_${this.namespace}${suffix}`
    }
}

// Export convenience instances for common namespaces
export const twoFactorLoginStorage = new TwoFactorStorage('login')
export const twoFactorSettingsStorage = new TwoFactorStorage('')
export const twoFactorSwitchStorage = new TwoFactorStorage('switch')
export const twoFactorDisableStorage = new TwoFactorStorage('disable')
export const twoFactorEnableStorage = new TwoFactorStorage('enable')
export const twoFactorAuthStorage = new TwoFactorStorage('auth')

/**
 * Result of checking lockout state from sessionStorage or database
 */
export interface LockoutCheckResult {
    // Verify lockout (wrong OTP attempts)
    isLockedOut: boolean
    lockoutCountdown: number
    lockoutDuration: number
    // Email rate limit (too many resend attempts)
    rateLimited: boolean
    rateLimitCountdown: number
    rateLimitDuration: number
    rateLimitMessage: string
    // Source of the data
    fromSession: boolean
}

/**
 * Interface for the 2FA auth status command
 */
export interface TwoFactorAuthStatusCommand {
    run(): Promise<{
        verifyRateLimited: boolean
        verifyLockoutSeconds: number
        emailRateLimited: boolean
        emailLockoutSeconds: number
    }>
}

/**
 * Check and restore lockout state from sessionStorage or database
 * This is a shared utility to avoid code duplication across 2FA settings pages
 *
 * @param storage - The storage instance for the specific page (settings, switch, disable)
 * @param command - The command to fetch auth status from database (optional, for API fallback)
 * @returns LockoutCheckResult with all lockout state, or null if no lockout is active
 */
export async function checkLockoutState(
    storage: TwoFactorStorage,
    command?: TwoFactorAuthStatusCommand
): Promise<LockoutCheckResult | null> {
    // First check sessionStorage (for lockouts from this session)
    const sessionLockout = storage.getLockout()
    const sessionRateLimit = storage.getRateLimit()

    if (sessionLockout || sessionRateLimit) {
        return {
            isLockedOut: sessionLockout?.isActive ?? false,
            lockoutCountdown: sessionLockout?.remainingSeconds ?? 0,
            lockoutDuration: sessionLockout?.duration ?? TWOFACTOR_RATE_LIMITS.VERIFY_LOCKOUT_SECONDS,
            rateLimited: sessionRateLimit?.isActive ?? false,
            rateLimitCountdown: sessionRateLimit?.remainingSeconds ?? 0,
            rateLimitDuration: sessionRateLimit?.duration ?? TWOFACTOR_RATE_LIMITS.EMAIL_SEND_LOCKOUT_SECONDS,
            rateLimitMessage: sessionRateLimit?.message ?? '',
            fromSession: true
        }
    }

    // No sessionStorage lockout - fetch from database if command provided
    if (!command) {
        return null
    }

    try {
        const authStatus = await command.run()

        const hasVerifyLockout = authStatus.verifyRateLimited && authStatus.verifyLockoutSeconds > 0
        const hasEmailLockout = authStatus.emailRateLimited && authStatus.emailLockoutSeconds > 0

        if (!hasVerifyLockout && !hasEmailLockout) {
            return null
        }

        // Save to sessionStorage for persistence during navigation
        if (hasVerifyLockout) {
            storage.saveLockout(authStatus.verifyLockoutSeconds)
        }
        if (hasEmailLockout) {
            storage.saveRateLimitSeconds(authStatus.emailLockoutSeconds, '')
        }

        return {
            isLockedOut: hasVerifyLockout,
            lockoutCountdown: authStatus.verifyLockoutSeconds,
            lockoutDuration: TWOFACTOR_RATE_LIMITS.VERIFY_LOCKOUT_SECONDS,
            rateLimited: hasEmailLockout,
            rateLimitCountdown: authStatus.emailLockoutSeconds,
            rateLimitDuration: TWOFACTOR_RATE_LIMITS.EMAIL_SEND_LOCKOUT_SECONDS,
            rateLimitMessage: '',
            fromSession: false
        }
    } catch {
        // Ignore errors - lockout check is not critical
        return null
    }
}
